import { Box, IconButton, Flex } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import InternationalOrderLine from '../../../containers/orders/InternationalOrderLine';
import { getIngredientOptions } from '../../../utils';
import ErrorMessage from './ErrorMessage';
import { sortInAlphabeticalOrder } from '../../../functions';
import { findEntitiesInState } from '../../../slices/masterData/entityManagerSlice';
import { connect } from 'react-redux';
import { cloneDeep } from 'lodash';
import { checkIfChicagoForm } from '../../lib/PurchaseOrderFunctions';
import { Alert } from '../../ui/alert';
import { MdAdd } from 'react-icons/md';
import Constants from '../../../Constants';

class InternationalOrderLines extends PureComponent {
  static propTypes = {
    buyCurrency: PropTypes.string,
    buyUnitOfMeasureId: PropTypes.number,
    form: PropTypes.instanceOf(Object),
    formType: PropTypes.string,
    grinderDestinationCountry: PropTypes.string.isRequired,
    inputProducts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        // Add other properties as needed
      })
    ).isRequired,
    isChilled: PropTypes.bool.isRequired,
    isInEditMode: PropTypes.bool.isRequired,
    hasExpectedProductionDate: PropTypes.bool,
    lineIndices: PropTypes.arrayOf(PropTypes.number).isRequired,
    lines: PropTypes.instanceOf(Object).isRequired,
    onAddLine: PropTypes.func.isRequired,
    onPPUChangeValue: PropTypes.func.isRequired,
    onPPURoundingDecimal: PropTypes.func.isRequired,
    onRemoveLine: PropTypes.func.isRequired,
    onUpdateLine: PropTypes.func.isRequired,
    packerId: PropTypes.string,
    packerPlantOriginCountry: PropTypes.string.isRequired,
    packerPlantId: PropTypes.string.isRequired,
    purchasingOffice: PropTypes.string,
    purchaseOrderId: PropTypes.number,
    purchaseOrderIndex: PropTypes.number.isRequired,
    requiredFields: PropTypes.instanceOf(Object).isRequired,
    requiredFieldsLabels: PropTypes.instanceOf(Object).isRequired,
    sellCurrency: PropTypes.string,
    sellUnitOfMeasureId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    shipmentDateEnd: PropTypes.string,
    submitProductionDetails: PropTypes.func,
    submitCLResults: PropTypes.func,
    submitPriceAdjustments: PropTypes.func,
    submitTemperatures: PropTypes.func,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
    packageWeights: PropTypes.arrayOf(PropTypes.Object),
    isUSADomestic: PropTypes.bool,
    isLocked: PropTypes.bool,
  };

  state = {
    validationErrors: {
      buyQuantity: false,
      dateError: null,
    },
  };

  setErrorState = (fieldName, errMsgOrStatus) => {
    const { validationErrors } = this.state;
    const validationErrorsCopy = cloneDeep(validationErrors);
    validationErrorsCopy[fieldName] = errMsgOrStatus;
    this.setState({ validationErrors: validationErrorsCopy });
  };

  render() {
    const { validationErrors } = this.state;
    const {
      buyCurrency,
      buyUnitOfMeasureId,
      form,
      formType,
      grinderDestinationCountry,
      hasExpectedProductionDate,
      inputProducts,
      isChilled,
      isInEditMode,
      lineIndices,
      lines,
      onAddLine,
      onPPUChangeValue,
      onPPURoundingDecimal,
      onRemoveLine,
      onUpdateLine,
      packerId,
      packerPlantOriginCountry,
      packerPlantId,
      purchasingOffice,
      purchaseOrderId,
      purchaseOrderIndex,
      requiredFields,
      sellCurrency,
      sellUnitOfMeasureId,
      shipmentDateEnd,
      submitProductionDetails,
      submitCLResults,
      submitTemperatures,
      submitPriceAdjustments,
      packageWeights,
      requiredFieldsLabels,
      isUSADomestic,
      isLocked,
      filteredPriceAgreementOptions,
    } = this.props;
    // exception allowing all AU/NZ products for Canadian packers to account for previous fake packers
    // Step 1: Get packer plant country code for matching
    const packerCountryLower = packerPlantOriginCountry?.toLowerCase();
    
    // Special case: Canada packer can access USA, Canada, Australia, New Zealand products
    const packerCountriesToMatch = packerCountryLower === 'canada'
      ? ['USA', 'Canada', 'Australia', 'New Zealand']
      : [packerPlantOriginCountry];
    
    const packerCountryCodes = Constants.COUNTRY_CODINGS.filter(x => 
      packerCountriesToMatch.some(country => x?.name?.toLowerCase() === country.toLowerCase())
    ).map(({ code }) => code);

    // Step 2: Apply grinder-based filtering for AU/NZ orders
    // This filters products based on grinder destination country AND packer plant country match
    let grinderFilteredProducts = inputProducts;
    
    if (grinderDestinationCountry && grinderDestinationCountry.trim()) {
      const grinderCountryLower = grinderDestinationCountry.toLowerCase().trim();
      const isChinaGrinder = grinderCountryLower === Constants.COUNTRIES.CHINA;
      const isRestrictedGrinder = Constants.NON_CHINA_GRINDER_COUNTRIES.some(
        country => country.toLowerCase().trim() === grinderCountryLower
      );

      if (isChinaGrinder) {
        // Scenario 1: China grinder
        // Show ONLY China-only products that match packer plant country
        grinderFilteredProducts = inputProducts.filter(product =>
          Constants.CHINA_ONLY_PRODUCT_UIDS.includes(product.uid) &&
          packerCountryCodes.includes(product.country)
        );
      } else if (isRestrictedGrinder) {
        // Scenario 2: Non-China grinder (restricted list)
        // EXCLUDE all China-only products, show remaining products that match packer plant country
        grinderFilteredProducts = inputProducts.filter(product =>
          !Constants.CHINA_ONLY_PRODUCT_UIDS.includes(product.uid) &&
          packerCountryCodes.includes(product.country)
        );
      } else {
        // Scenario 3: All other grinders
        // Show all products that match packer plant country
        grinderFilteredProducts = inputProducts.filter(product =>
          packerCountryCodes.includes(product.country)
        );
      }
    } else {
      // If no grinder selected, show products matching packer plant country
      grinderFilteredProducts = inputProducts.filter(product =>
        packerCountryCodes.includes(product.country)
      );
    }

    // Step 3: Convert to options format
    const ingredientsOptions = grinderFilteredProducts
      ?.filter(product => !product.delisted)
      .map(el => ({ value: el.uid, label: el.name, delisted: el.delisted, cl: el.cl }));

    ingredientsOptions.sort((a, b) => {
      const packerA = a.label?.toLowerCase();

      const packerB = b.label?.toLowerCase();
      return sortInAlphabeticalOrder(packerA, packerB);
    });

    const activeLineIndices = lineIndices?.filter(lineIdx => {
      return lines[lineIdx]?.active;
    });

    const isChicagoForm = checkIfChicagoForm({
      purchasingOffice: form.purchasingOffice,
      invoicingOffice: form.invoicingOffice,
    });

    const hasNoActiveLine = lineIndices?.every(lineIdx => !lines[lineIdx].active);

    return (
      <>
        {hasNoActiveLine && (
          <Flex justifyContent="flex-end" marginTop="0px !important">
            <IconButton
              onClick={onAddLine}
              disabled={!isInEditMode}
              colorScheme="actionSecondary"
              height="25px"
              minWidth="25px"
              borderRadius="32px"
            >
              <MdAdd className="international-order-line__icon--orange" />
            </IconButton>
          </Flex>
        )}
        <Box key={lines} backgroundColor="inherit">
          {lineIndices?.map(lineIdx => {
            const line = lines[lineIdx];
            const lineProps = {
              buyCurrency,
              buyUnitOfMeasureId,
              destinationCountry: grinderDestinationCountry,
              formType,
              form,
              ingredientsOptions,
              isChilled,
              isInEditMode: isInEditMode && line.active,
              hasExpectedProductionDate,
              lineIndices: activeLineIndices,
              line,
              lines,
              onAddLine,
              onUpdateLine,
              onRemoveLine,
              onPPUChangeValue,
              onPPURoundingDecimal,
              originCountry: packerPlantOriginCountry,
              packerId,
              packerPlantId,
              purchasingOffice,
              purchaseOrderId,
              purchaseOrderIndex,
              requiredFields,
              sellCurrency,
              sellUnitOfMeasureId,
              setErrorState: this.setErrorState,
              shipmentDateEnd,
              submitProductionDetails,
              submitCLResults,
              submitTemperatures,
              submitPriceAdjustments,
              packageWeights,
              requiredFieldsLabels,
              isUSADomestic,
              isChicagoForm,
              isLocked,
              filteredPriceAgreementOptions,
            };
            return <InternationalOrderLine key={lineIdx} {...lineProps} />;
          })}
          <Box className="col-md-12" style={{ paddingTop: '10px', marginLeft: 'none' }}>
            <ErrorMessage form={form} buyQuantityError={validationErrors.buyQuantity} {...this.props} />
            {validationErrors.dateError && <Alert status="error" title={validationErrors.dateError} />}
          </Box>
        </Box>
      </>
    );
  }
}

const mapStateToProps = state => {
  const { load_sizes: loadSizes } = findEntitiesInState(state);
  return { loadSizes };
};

export default connect(mapStateToProps)(InternationalOrderLines);
